import { useRoute, useRuntimeConfig } from '#imports'
import { type ComputedRef, computed } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

/**
 * Gates the web shopping assistant (header search entry point + the
 * layout-mounted host).
 *
 * Backed by the `experiment.shoppingAssistantWeb` CloudBees experiment rather
 * than a runtime feature flag, so the rollout can be scoped to a QA audience
 * via targeting. Returns true only for the `withAssistant` variation.
 *
 * Also gated by FF_SHOPPING_ASSISTANT_ENABLED_COUNTRIES, independently of
 * experiment assignment, so availability can be restricted per country.
 *
 * Routes can opt out entirely via `meta.shoppingAssistant.enabled: false`
 * (e.g. /help, which already has its own floating action button). Returned
 * as a computed so callers stay correct across client-side navigation
 * regardless of whether the target route remounts the calling component.
 */
export function useIsShoppingAssistantWebEnabled(): ComputedRef<boolean> {
  const route = useRoute()
  const { market } = useMarketplace()
  const { FF_SHOPPING_ASSISTANT_ENABLED_COUNTRIES } = useRuntimeConfig().public
  const experiments = useExperiments()

  return computed(() => {
    if (route.meta?.shoppingAssistant?.enabled === false) {
      return false
    }

    const isInExperiment =
      experiments['experiment.shoppingAssistantWeb'] === 'withAssistant'
    const isCountryEnabled = Boolean(
      FF_SHOPPING_ASSISTANT_ENABLED_COUNTRIES?.includes(market.countryCode),
    )

    return isInExperiment && isCountryEnabled
  })
}
